/**
 * OcrConflictModal — shown when the user replaces an already-uploaded OCR
 * document and the new extraction yields values that differ from the data
 * currently in the form (manual edits, or earlier OCR runs).
 *
 * The user resolves each conflict per-field: keep the old (current form)
 * value or accept the new (just-extracted) value. The modal returns the
 * subset of fields the caller should overwrite via `updateSection`.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle, ArrowRight, Check, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export type OcrConflictSection =
  | 'businessInfo'
  | 'taxLegalInfo'
  | 'bankAccountInfo'
  | 'representativeInfo';

export interface OcrConflict {
  section: OcrConflictSection;
  field: string;
  label: string;
  oldValue: string;
  newValue: string;
}

export type OcrConflictChoice = 'old' | 'new';

interface OcrConflictModalProps {
  open: boolean;
  conflicts: OcrConflict[];
  /** Localized doc name (used as subtitle context). */
  documentLabel: string;
  onClose: () => void;
  /** Called with the resolved choices (per index). Modal closes after. */
  onResolve: (choices: OcrConflictChoice[]) => void;
}

const OcrConflictModal: React.FC<OcrConflictModalProps> = ({
  open,
  conflicts,
  documentLabel,
  onClose,
  onResolve,
}) => {
  const { t } = useTranslation();
  const [choices, setChoices] = useState<OcrConflictChoice[]>([]);

  // Reset choices whenever a new conflict batch is opened. Default is
  // "keep old" because we never want OCR to silently overwrite manual edits.
  useEffect(() => {
    if (open) {
      setChoices(conflicts.map(() => 'old'));
    }
  }, [open, conflicts]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [open, onClose]);

  const allOld = useMemo(() => choices.every((c) => c === 'old'), [choices]);
  const allNew = useMemo(() => choices.every((c) => c === 'new'), [choices]);

  if (typeof document === 'undefined') return null;

  const setAll = (v: OcrConflictChoice) => setChoices(conflicts.map(() => v));
  const setOne = (i: number, v: OcrConflictChoice) => {
    setChoices((prev) => prev.map((c, idx) => (idx === i ? v : c)));
  };

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4"
        >
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.97 }}
            transition={{ type: 'spring', damping: 26, stiffness: 320 }}
            className="relative w-full sm:max-w-2xl max-h-[92vh] bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ocr-conflict-title"
          >
            <header className="flex items-start gap-3 px-5 sm:px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-amber-50 to-white">
              <span className="w-10 h-10 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center flex-shrink-0">
                <AlertTriangle size={20} />
              </span>
              <div className="flex-1 min-w-0">
                <h2
                  id="ocr-conflict-title"
                  className="text-base sm:text-lg font-semibold text-gray-900"
                >
                  {t('documents.conflict.title', {
                    defaultValue: 'The new document has different details',
                  })}
                </h2>
                <p className="text-xs sm:text-sm text-gray-600 mt-0.5 break-words">
                  {t('documents.conflict.subtitle', {
                    document: documentLabel,
                    defaultValue:
                      'We extracted new values from "{{document}}" that differ from what is already in the form. Pick which value to keep for each field.',
                  })}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="close"
                className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <X size={18} />
              </button>
            </header>

            <div className="px-5 sm:px-6 py-3 flex items-center gap-2 border-b border-gray-100">
              <button
                type="button"
                onClick={() => setAll('old')}
                className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
                  allOld
                    ? 'bg-gray-100 text-gray-800 border-gray-200'
                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}
              >
                {t('documents.conflict.keepAllOld', { defaultValue: 'Keep all current values' })}
              </button>
              <button
                type="button"
                onClick={() => setAll('new')}
                className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
                  allNew
                    ? 'bg-haat-red-50 text-haat-red-700 border-haat-red-100'
                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}
              >
                {t('documents.conflict.useAllNew', { defaultValue: 'Use all new values' })}
              </button>
            </div>

            <div className="px-5 sm:px-6 py-4 overflow-y-auto flex-1">
              <ul className="flex flex-col gap-3">
                {conflicts.map((c, i) => {
                  const choice = choices[i] ?? 'old';
                  return (
                    <li
                      key={`${c.section}.${c.field}`}
                      className="rounded-2xl border border-gray-200 overflow-hidden"
                    >
                      <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200">
                        <p className="text-xs font-semibold uppercase tracking-wider text-gray-600">
                          {c.label}
                        </p>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2">
                        <button
                          type="button"
                          onClick={() => setOne(i, 'old')}
                          className={`text-start p-3.5 transition-colors border-b sm:border-b-0 sm:border-e ${
                            choice === 'old'
                              ? 'bg-gray-50 border-gray-200'
                              : 'bg-white border-gray-100 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                              {t('documents.conflict.currentValue', {
                                defaultValue: 'Current value',
                              })}
                            </span>
                            {choice === 'old' && (
                              <span className="w-5 h-5 rounded-full bg-gray-800 text-white flex items-center justify-center">
                                <Check size={12} />
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-800 break-words">
                            {c.oldValue || (
                              <span className="text-gray-300">—</span>
                            )}
                          </p>
                        </button>
                        <button
                          type="button"
                          onClick={() => setOne(i, 'new')}
                          className={`text-start p-3.5 transition-colors ${
                            choice === 'new'
                              ? 'bg-haat-red-50/60'
                              : 'bg-white hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <span className="text-[11px] font-semibold uppercase tracking-wider text-haat-red-600">
                              {t('documents.conflict.newValue', {
                                defaultValue: 'New value from document',
                              })}
                            </span>
                            {choice === 'new' && (
                              <span className="w-5 h-5 rounded-full bg-haat-red-600 text-white flex items-center justify-center">
                                <Check size={12} />
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-900 font-medium break-words">
                            {c.newValue}
                          </p>
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>

            <footer className="px-5 sm:px-6 py-3 border-t border-gray-100 flex items-center justify-between gap-3 bg-gray-50">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-xl text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-100 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={() => onResolve(choices)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-haat-red-600 hover:bg-haat-red-700 text-white shadow-sm transition-colors"
              >
                <span>
                  {t('documents.conflict.apply', {
                    defaultValue: 'Apply choices',
                  })}
                </span>
                <ArrowRight size={16} />
              </button>
            </footer>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default OcrConflictModal;
